import { Bike, ChevronRight, Clock, Pencil, Store, Zap } from 'lucide-react';

/**
 * Fulfillment details with FULL order-config parity to the ordering app: the
 * customer can switch pickup ↔ delivery, change the time (ASAP or scheduled)
 * and edit the delivery address without leaving checkout. The time label is
 * the LIVE evaluation from useOrderTimeAwareness — ASAP re-derives its ready
 * estimate against the real clock every 30s, and a scheduled slot that passes
 * surfaces an inline recovery row (switch to ASAP / pick a new time) instead
 * of a dead-end "go back to the menu".
 *
 * All handlers are optional — without them the rows render as static text
 * (used while the cart/restaurant are still resolving).
 *
 * `layout` picks ONE rendering. This used to emit both (a desktop section and a
 * phone stack, each hidden by breakpoint classes) from every instance, and the
 * checkout page mounts two instances — so on a phone the "desktop" instance's
 * phone half showed too, repeating the pickup and time rows under the order
 * summary. The page decides where each instance is visible; this component
 * decides only what one looks like.
 */
interface PickupDetailsProps {
    /**
     * `section` — titled, grouped panel (the desktop left column).
     * `stack` — individual cards for the phone summary card.
     */
    layout: 'section' | 'stack';
    address: string;
    orderType?: 'pickup' | 'delivery';
    /** Live fulfillment-time label (e.g. "ASAP · ready ~7:45 PM", "Today 8:00 PM"). */
    deliveryTime?: string;
    /** Whether delivery is offered (courier configured) — shows the type switch. */
    deliveryOffered?: boolean;
    /** True while a config change is persisting — disables the controls. */
    updating?: boolean;
    /** Warning when the current time is no longer valid (passed / too soon / closed). */
    timeWarning?: string;
    /** Whether ASAP is available right now (gates the quick-recovery action). */
    asapAvailable?: boolean;
    onSelectPickup?: () => void;
    onSelectDelivery?: () => void;
    onChangeTime?: () => void;
    onSwitchToAsap?: () => void;
    /** Edit the delivery address (delivery only). */
    onEditAddress?: () => void;
}

export default function PickupDetails({
    layout,
    address,
    orderType = 'pickup',
    deliveryTime = 'As soon as possible',
    deliveryOffered = false,
    updating = false,
    timeWarning,
    asapAvailable = false,
    onSelectPickup,
    onSelectDelivery,
    onChangeTime,
    onSwitchToAsap,
    onEditAddress,
}: PickupDetailsProps) {
    const isPickup = orderType === 'pickup';
    const typeLabel = isPickup ? 'Pick up from' : 'Deliver to';

    const interactive = Boolean(onChangeTime);

    const pill = (active: boolean) =>
        `flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-colors ${
            active ? 'bg-red-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
        } disabled:cursor-not-allowed disabled:opacity-60`;

    const typeSwitch = deliveryOffered && onSelectPickup && onSelectDelivery && (
        <div className="grid grid-cols-2 gap-1 bg-white p-1 rounded-xl border border-gray-200">
            <button
                type="button"
                onClick={onSelectPickup}
                disabled={updating}
                className={pill(isPickup)}
                aria-pressed={isPickup}
            >
                <Store className="h-4 w-4" /> Pickup
            </button>
            <button
                type="button"
                onClick={onSelectDelivery}
                disabled={updating}
                className={pill(!isPickup)}
                aria-pressed={!isPickup}
            >
                <Bike className="h-4 w-4" /> Delivery
            </button>
        </div>
    );

    const addressRow = (
        <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
                {isPickup ? (
                    <Store className="h-5 w-5 text-gray-600 mt-0.5 shrink-0" />
                ) : (
                    <Bike className="h-5 w-5 text-gray-600 mt-0.5 shrink-0" />
                )}
                <span className="text-gray-900 font-medium leading-tight">
                    {typeLabel} {address}
                </span>
            </div>
            {!isPickup && onEditAddress && (
                <button
                    type="button"
                    onClick={onEditAddress}
                    disabled={updating}
                    aria-label="Edit delivery address"
                    className="flex shrink-0 items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-60"
                >
                    <Pencil className="h-3.5 w-3.5" aria-hidden /> Edit
                </button>
            )}
        </div>
    );

    const timeRow = interactive ? (
        <button
            type="button"
            onClick={onChangeTime}
            disabled={updating}
            className={`flex w-full items-center justify-between gap-3 rounded-xl border bg-white px-3 py-2.5 text-left transition-colors disabled:opacity-60 ${
                timeWarning
                    ? 'border-amber-300 hover:border-amber-400'
                    : 'border-gray-200 hover:border-gray-300'
            }`}
        >
            <span className="flex items-center gap-3 min-w-0">
                <Clock className={`h-5 w-5 shrink-0 ${timeWarning ? 'text-amber-600' : 'text-gray-600'}`} />
                <span className={`font-medium ${timeWarning ? 'text-amber-800' : 'text-gray-900'}`}>
                    {deliveryTime}
                </span>
            </span>
            <span className="flex shrink-0 items-center gap-1 text-sm font-medium text-gray-600">
                Change <ChevronRight className="h-4 w-4 text-gray-400" aria-hidden />
            </span>
        </button>
    ) : (
        <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-gray-600" />
            <span className="text-gray-900 font-medium">{deliveryTime}</span>
        </div>
    );

    // A passed / too-soon slot (or now-closed kitchen) gets calm inline
    // recovery actions — never a dead end that sends the customer back.
    const warningRow = timeWarning && interactive && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5" role="alert">
            <p className="text-sm text-amber-800">{timeWarning}</p>
            <div className="mt-2 flex items-center gap-2">
                {asapAvailable && onSwitchToAsap && (
                    <button
                        type="button"
                        onClick={onSwitchToAsap}
                        disabled={updating}
                        className="flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-gray-800 disabled:opacity-60"
                    >
                        <Zap className="h-3.5 w-3.5" aria-hidden />
                        {updating ? 'Updating…' : 'Switch to ASAP'}
                    </button>
                )}
                {onChangeTime && (
                    <button
                        type="button"
                        onClick={onChangeTime}
                        disabled={updating}
                        className="rounded-lg border border-amber-300 px-3 py-1.5 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-100 disabled:opacity-60"
                    >
                        Pick a new time
                    </button>
                )}
            </div>
        </div>
    );

    if (layout === 'section') {
        return (
            <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    {isPickup ? 'Pickup details' : 'Delivery details'}
                </h2>
                <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
                    {typeSwitch}
                    {addressRow}
                    {timeRow}
                    {warningRow}
                </div>
            </div>
        );
    }

    // Individual cards matching the other rows of the phone summary card.
    return (
        <div className="space-y-3">
            {typeSwitch && (
                <div className="p-1 bg-white rounded-2xl">{typeSwitch}</div>
            )}
            <div className="p-4 bg-white rounded-2xl">{addressRow}</div>
            <div className={interactive ? '' : 'p-4 bg-white rounded-2xl'}>
                {timeRow}
            </div>
            {warningRow}
        </div>
    );
}
